import { useState } from 'react';
import { ENDORSEMENTS, EXPERT_CONSENSUS, type Endorsement } from '../data/endorsements';

const PORTRAIT_WIDTH = 720;
const PORTRAIT_HEIGHT = 864;

/** Two-letter initials from a name ("Dr. Emma van der Berg" → "EV", "Mark & Lisa" → "ML") */
function initials(name: string): string {
  return name
    .replace(/^Dr\.\s*/i, '')
    .split(/[\s&]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Stable hue derived from name for the initials-fallback avatar */
function avatarHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ((hash % 360) + 360) % 360;
}

function VerifiedBadge() {
  return (
    <svg
      className="h-[16px] w-[16px] shrink-0"
      viewBox="0 0 22 22"
      fill="none"
      aria-label="Verified professional"
    >
      <path
        d="M9.35 1.636a2 2 0 012.3 0l1.13.796a2 2 0 001.07.363l1.38.08a2 2 0 011.895 1.895l.08 1.38a2 2 0 00.363 1.07l.796 1.13a2 2 0 010 2.3l-.796 1.13a2 2 0 00-.363 1.07l-.08 1.38a2 2 0 01-1.895 1.895l-1.38.08a2 2 0 00-1.07.363l-1.13.796a2 2 0 01-2.3 0l-1.13-.796a2 2 0 00-1.07-.363l-1.38-.08a2 2 0 01-1.895-1.895l-.08-1.38a2 2 0 00-.363-1.07l-.796-1.13a2 2 0 010-2.3l.796-1.13a2 2 0 00.363-1.07l.08-1.38A2 2 0 015.77 2.875l1.38-.08a2 2 0 001.07-.363l1.13-.796z"
        fill="#F5C842"
      />
      <path
        d="M8 10l2.2 2.2 3.8-4.6"
        stroke="#1A0B2E"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Portrait({ endorsement }: { endorsement: Endorsement }) {
  const [imgFailed, setImgFailed] = useState(false);
  const hue = avatarHue(endorsement.name);
  const showImage = endorsement.image && !imgFailed;
  const fallbackStyle = showImage
    ? undefined
    : {
        background: `linear-gradient(145deg, hsla(${hue}, 44%, 62%, 0.34), hsla(${hue + 32}, 38%, 48%, 0.2))`,
      };

  return (
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-white/[0.04] shadow-[0_0_0_4px_rgba(255,255,255,0.04)]"
      data-endorsement-image="true"
      style={fallbackStyle}
    >
      {showImage ? (
        <img
          src={endorsement.image}
          alt={endorsement.name}
          className="h-full w-full object-cover"
          width={PORTRAIT_WIDTH}
          height={PORTRAIT_HEIGHT}
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-4xl font-semibold tracking-[0.18em]"
          style={{ color: `hsla(${hue}, 62%, 84%, 0.92)` }}
        >
          {initials(endorsement.name)}
        </div>
      )}
    </div>
  );
}

function EndorsementAttribution({ endorsement }: { endorsement: Endorsement }) {
  return (
    <div
      className="mt-auto flex items-center gap-3 border-t border-white/[0.05] pt-4"
      data-endorsement-attribution="true"
    >
      <Portrait endorsement={endorsement} />

      <div className="min-w-0">
        <p
          className="flex items-center gap-1.5 whitespace-nowrap text-[0.95rem] font-bold tracking-[-0.01em] text-white"
          data-endorsement-name="true"
        >
          {endorsement.name}
          <VerifiedBadge />
        </p>

        <p className="mt-1 whitespace-nowrap text-[12px] font-medium text-white/55">
          {endorsement.credential && (
            <>
              <span data-endorsement-credential="true">{endorsement.credential}</span>
              <span className="text-white/30">, </span>
            </>
          )}
          <span data-endorsement-title="true">{endorsement.title}</span>
        </p>
      </div>
    </div>
  );
}

function EndorsementCard({ endorsement }: { endorsement: Endorsement }) {
  return (
    <article
      className="flex h-full flex-col rounded-[2rem] border border-white/10 bg-purple-card p-6 sm:p-7"
      data-endorsement-card="true"
    >
      <blockquote
        className="flex-1 max-w-[32ch] pb-3 text-[16px] leading-7 text-slate-100 sm:text-[17px]"
        data-endorsement-quote="true"
      >
        <span className="block text-[2rem] leading-none text-white/20">&ldquo;</span>
        <p className="mt-3 italic">{endorsement.quote}&rdquo;</p>
      </blockquote>

      <EndorsementAttribution endorsement={endorsement} />
    </article>
  );
}

export default function ProfessionalEndorsements() {
  return (
    <section id="expert-endorsements" className="mb-24 scroll-mt-28">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-extrabold mb-3 tracking-[-0.03em]">
          Designed with experts
        </h2>
        <p className="mx-auto max-w-2xl text-sm leading-relaxed text-slate-400">
          {EXPERT_CONSENSUS}
        </p>
      </div>

      {ENDORSEMENTS.length > 0 && (
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
          {ENDORSEMENTS.map((e) => (
            <EndorsementCard key={e.name} endorsement={e} />
          ))}
        </div>
      )}
    </section>
  );
}
